/**
 * Definición de cada pestaña de "Creditos y Movimientos".
 *
 * Todo lo que cambia entre los seis módulos vive aquí (endpoints, columnas, filtros, textos), para
 * que <TablaGlobalNomina /> sea uno solo y no seis copias. Las columnas son `columns` de antd
 * normales y salen de las que ya pintan los componentes por-empleado del Sobre Recibo, más la
 * columna "Empleado" que sólo tiene sentido en la vista global.
 *
 * Cada módulo declara DOS juegos de columnas:
 *   · `columnas(ctx)`      — las de antd, que devuelven JSX (<Tag>, renglones de dos líneas).
 *   · `columnasExport(ctx)` — las mismas en plano, para el Excel y el PDF. Existen aparte porque
 *     el JSX no se puede meter ni en una celda de xlsx-js-style ni en una tabla de pdfmake.
 * Las dos tienen que moverse juntas: si se agrega una columna a la tabla, va también en el export.
 */

import React from 'react'
import { Tag } from 'antd'

// ─── Formato ──────────────────────────────────────────────────────────────────

export const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0)

export const fmtFecha = (f) => {
  if (!f) return '—'
  const p = (f + '').split('T')[0].split('-')
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (f + '')
}

const fmtNum = (n) => (n === null || n === undefined || n === '' ? '—' : n)

/**
 * Mismo formato del factor que usa InfonvitEmpleado: cada tipo de crédito guarda una cosa
 * distinta en el mismo campo (cuota fija en pesos, VSM adimensional, o fracción de porcentaje).
 */
const fmtFactor = (tipoCredito, factor) => {
  if (factor === null || factor === undefined || factor === '') return '—'
  const n = parseFloat(factor)
  if (isNaN(n)) return '—'
  if (tipoCredito === 'porcentaje') return (n * 100).toFixed(2) + '%'
  if (tipoCredito === 'factor_descuento') return n.toFixed(4) + ' VSM'
  if (tipoCredito === 'cuota_fija') return fmtMXN(n)
  return String(factor)
}

// ─── Columnas comunes ─────────────────────────────────────────────────────────

const COL_EMPLEADO = {
  title: 'Empleado',
  key: 'empleado',
  fixed: 'left',
  width: 230,
  render: (_, r) => (
    <div style={{ lineHeight: 1.3 }}>
      <div style={{ fontSize: 12 }}>{r.empleadoNombre || '—'}</div>
      {r.empleadoCodigo !== undefined && r.empleadoCodigo !== null && (
        <div style={{ fontSize: 10, color: '#999' }}>Código {r.empleadoCodigo}</div>
      )}
    </div>
  ),
}

/** Etiqueta de estatus. `extra` permite agregar los valores propios de un módulo (el 2). */
const colEstatus = (etiquetas) => ({
  title: 'Estatus',
  dataIndex: 'estatus',
  key: 'estatus',
  width: 100,
  render: (v) => {
    const e = etiquetas[v] || { texto: String(v ?? '—') }
    return <Tag color={e.color}>{e.texto}</Tag>
  },
})

// ─── Columnas de exportación ──────────────────────────────────────────────────

/**
 * Forma de una columna de exportación:
 *   titulo   — encabezado.
 *   tipo     — 'texto' | 'centro' | 'numero' | 'moneda' | 'fecha', o (r) => tipo para las mixtas.
 *   valor    — (r) => valor CRUDO: número en 'numero'/'moneda', ISO en 'fecha', texto ya armado
 *              en 'texto'/'centro'. El formato lo aplica el exportador, no esta función.
 *   ancho    — `wch` de la columna en Excel.
 *   anchoPdf — puntos de pdfmake ('*' reparte lo que sobre). El ancho útil en LETTER horizontal
 *              con márgenes de 22 pt es ~748.
 *   suma     — las de 'moneda' se totalizan siempre; una de 'numero' sólo con esta bandera.
 */

/** La columna "Empleado" de pantalla son dos renglones; en una hoja valen más como dos columnas. */
const COLS_EXPORT_EMPLEADO = [
  { titulo: 'Código', tipo: 'centro', ancho: 9, anchoPdf: 34, valor: (r) => r.empleadoCodigo },
  { titulo: 'Empleado', tipo: 'texto', ancho: 34, anchoPdf: '*', valor: (r) => r.empleadoNombre },
]

const colExportEstatus = (etiquetas) => ({
  titulo: 'Estatus',
  tipo: 'centro',
  ancho: 12,
  anchoPdf: 48,
  valor: (r) => (etiquetas[r.estatus] || {}).texto || '',
})

const ESTATUS_ACTIVO_INACTIVO = {
  1: { texto: 'Activo', color: 'green' },
  0: { texto: 'Inactivo' },
}

const ESTATUS_CON_COMPLETADO = {
  ...ESTATUS_ACTIVO_INACTIVO,
  2: { texto: 'Completado', color: 'blue' },
}

const OPCIONES_ACTIVO_INACTIVO = [
  { valor: '1', label: 'Activos' },
  { valor: '0', label: 'Inactivos' },
]

// ─── Etiquetas por módulo ─────────────────────────────────────────────────────

const TIPO_CREDITO_LABEL = {
  porcentaje: 'Porcentaje',
  factor_descuento: 'Factor de Descuento',
  cuota_fija: 'Cuota Fija',
}

const CALCULO_FONACOT_LABEL = {
  importe_fijo: 'Importe Fijo',
  proporcion_dias: 'Prop. Días Trabajados',
}

const TIPO_CONCEPTO_LABEL = { percepcion: 'Percepción', deduccion: 'Deducción' }
const TIPO_VALOR_LABEL = { importe: 'Importe $', valor: 'Valor' }
const TIPO_PATRON_LABEL = { diaPeriodo: 'Día del periodo', diaSemana: 'Día de la semana' }
const TIPO_VACACION_LABEL = {
  vacaciones_y_prima: 'Vac + Prima',
  solo_prima: 'Solo Prima',
  solo_vacaciones: 'Solo Vacaciones',
}
const RAMO_SEGURO_LABEL = {
  '01': '01 Riesgo de trabajo',
  '02': '02 Enfermedad general',
  '03': '03 Maternidad',
  '04': '04 Licencia hijos con cáncer',
}

const DIAS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']

/** "Sáb: 2 · Dom: 1" o "Día 3: 2", según el tipo de patrón. */
const resumenPatron = (registro) => {
  const dias = registro.dias || []
  if (!dias.length) return '—'
  return dias
    .map((d) => {
      const etiqueta = registro.tipoPatron === 'diaSemana'
        ? (DIAS_ES[d.clave] ?? d.clave)
        : `Día ${d.clave}`
      return `${etiqueta}: ${d.cantidad}`
    })
    .join(' · ')
}

const nombreConcepto = (r) => {
  const c = r.concepto
  if (!c) return '—'
  return `${c.serieNumero || c.numero || ''} - ${c.descripcion || ''}`.trim()
}

const nombreTipoIncidencia = (r) => {
  const t = r.tipoIncidencia
  if (!t) return '—'
  return `${t.clave || ''} - ${t.nombre || ''}`.trim()
}

// ─── Definiciones ─────────────────────────────────────────────────────────────

export const DEFINICIONES = [
  {
    clave: 'infonavit',
    titulo: 'Créditos INFONAVIT',
    endpointListado: 'nomina/creditosInfonvitEmpresa',
    endpointCancelar: 'nomina/cancelarCreditoInfonvit',
    cuerpoCancelar: (r, empresaId) => ({ creditoId: r._id, empresaId }),
    tituloCancelar: '¿Inactivar este crédito Infonavit?',
    textoCancelar: 'Ya no se aplicará en futuros períodos.',
    etiquetasEstatus: ESTATUS_ACTIVO_INACTIVO,
    opcionesEstatus: OPCIONES_ACTIVO_INACTIVO,
    soportaVigentes: true,
    ayudaVigentes: 'Sólo los que ya alcanzaron su fecha de aplicación',
    anchoTabla: 1400,
    filtrosExtra: () => [
      {
        clave: 'tipoCredito',
        placeholder: 'Todos los tipos',
        opciones: Object.entries(TIPO_CREDITO_LABEL).map(([valor, label]) => ({ valor, label })),
      },
    ],
    columnas: () => [
      COL_EMPLEADO,
      { title: 'No. Crédito', dataIndex: 'numeroCreditoInfonvit', key: 'num', width: 130 },
      { title: 'Descripción', dataIndex: 'descripcion', key: 'desc', width: 180, ellipsis: true },
      {
        title: 'Tipo', dataIndex: 'tipoCredito', key: 'tipo', width: 150,
        render: (v) => TIPO_CREDITO_LABEL[v] || v || '—',
      },
      {
        title: 'Factor', key: 'factor', width: 120, align: 'right',
        render: (_, r) => fmtFactor(r.tipoCredito, r.factor),
      },
      {
        title: 'Monto Crédito', dataIndex: 'montoCredito', key: 'montoCredito',
        width: 130, align: 'right', render: fmtMXN,
      },
      {
        title: 'Acumulado', dataIndex: 'montoAcumulado', key: 'montoAcumulado',
        width: 130, align: 'right', render: fmtMXN,
      },
      {
        title: 'V. Aplicado', dataIndex: 'vecesAplicado', key: 'vecesAplicado',
        width: 90, align: 'right', render: fmtNum,
      },
      {
        title: 'Seguro', dataIndex: 'incluirSeguro', key: 'seguro', width: 80, align: 'center',
        render: (v) => (v ? <Tag color="blue">Sí</Tag> : '—'),
      },
      {
        title: 'F. Aplicación', dataIndex: 'fechaAplicacion', key: 'fechaAplicacion',
        width: 115, render: fmtFecha,
      },
      colEstatus(ESTATUS_ACTIVO_INACTIVO),
    ],
    columnasExport: () => [
      ...COLS_EXPORT_EMPLEADO,
      { titulo: 'No. Crédito', tipo: 'texto', ancho: 15, anchoPdf: 66, valor: (r) => r.numeroCreditoInfonvit },
      { titulo: 'Descripción', tipo: 'texto', ancho: 26, anchoPdf: 90, valor: (r) => r.descripcion },
      {
        titulo: 'Tipo', tipo: 'texto', ancho: 18, anchoPdf: 74,
        valor: (r) => TIPO_CREDITO_LABEL[r.tipoCredito] || r.tipoCredito || '',
      },
      // Mezcla unidades según el tipo de crédito (%, VSM o pesos): va como texto, no se suma.
      {
        titulo: 'Factor', tipo: 'centro', ancho: 12, anchoPdf: 54,
        valor: (r) => fmtFactor(r.tipoCredito, r.factor),
      },
      { titulo: 'Monto Crédito', tipo: 'moneda', ancho: 15, anchoPdf: 62, valor: (r) => r.montoCredito },
      { titulo: 'Acumulado', tipo: 'moneda', ancho: 15, anchoPdf: 62, valor: (r) => r.montoAcumulado },
      { titulo: 'V. Aplicado', tipo: 'numero', ancho: 11, anchoPdf: 42, valor: (r) => r.vecesAplicado },
      { titulo: 'Seguro', tipo: 'centro', ancho: 8, anchoPdf: 34, valor: (r) => (r.incluirSeguro ? 'Sí' : '') },
      { titulo: 'F. Aplicación', tipo: 'fecha', ancho: 13, anchoPdf: 55, valor: (r) => r.fechaAplicacion },
      colExportEstatus(ESTATUS_ACTIVO_INACTIVO),
    ],
  },

  {
    clave: 'fonacot',
    titulo: 'Créditos FONACOT',
    endpointListado: 'nomina/creditosFonacotEmpresa',
    endpointCancelar: 'nomina/cancelarCreditoFonacot',
    cuerpoCancelar: (r, empresaId) => ({ creditoId: r._id, empresaId }),
    tituloCancelar: '¿Inactivar este crédito FONACOT?',
    textoCancelar: 'Ya no se aplicará en futuros períodos.',
    etiquetasEstatus: ESTATUS_ACTIVO_INACTIVO,
    opcionesEstatus: OPCIONES_ACTIVO_INACTIVO,
    soportaVigentes: true,
    ayudaVigentes: 'Sólo los que ya alcanzaron su fecha de aplicación',
    // Un FONACOT sin fecha de aplicación es invisible para el motor: el filtro los aísla.
    filtroProblemas: 'Sólo sin fecha de aplicación',
    anchoTabla: 1500,
    filtrosExtra: () => [
      {
        clave: 'calculoRetencion',
        placeholder: 'Todos los cálculos',
        opciones: Object.entries(CALCULO_FONACOT_LABEL).map(([valor, label]) => ({ valor, label })),
      },
    ],
    columnas: () => [
      COL_EMPLEADO,
      { title: 'No. Crédito', dataIndex: 'numeroCreditoFonacot', key: 'num', width: 130 },
      { title: 'Concepto', key: 'concepto', width: 190, ellipsis: true, render: (_, r) => nombreConcepto(r) },
      {
        title: 'Cálculo', dataIndex: 'calculoRetencion', key: 'calculo', width: 160,
        render: (v) => CALCULO_FONACOT_LABEL[v] || v || '—',
      },
      {
        title: 'Importe Crédito', dataIndex: 'importeCredito', key: 'importeCredito',
        width: 130, align: 'right', render: fmtMXN,
      },
      {
        title: 'Importe/Periodo', dataIndex: 'importeFijoPeriodo', key: 'importeFijoPeriodo',
        width: 130, align: 'right', render: fmtMXN,
      },
      {
        title: 'Acumulado', dataIndex: 'montoAcumulado', key: 'montoAcumulado',
        width: 120, align: 'right', render: fmtMXN,
      },
      {
        title: 'Saldo Actual', dataIndex: 'saldoActual', key: 'saldoActual',
        width: 120, align: 'right', render: fmtMXN,
      },
      {
        title: 'F. Aplicación', key: 'fechaAplicacion', width: 190,
        render: (_, r) => (
          r.sinFechaAplicacion
            ? <Tag color="red">Sin fecha — no se aplicará</Tag>
            : fmtFecha(r.fechaAplicacion)
        ),
      },
      colEstatus(ESTATUS_ACTIVO_INACTIVO),
    ],
    columnasExport: () => [
      ...COLS_EXPORT_EMPLEADO,
      { titulo: 'No. Crédito', tipo: 'texto', ancho: 15, anchoPdf: 62, valor: (r) => r.numeroCreditoFonacot },
      { titulo: 'Concepto', tipo: 'texto', ancho: 26, anchoPdf: 95, valor: (r) => nombreConcepto(r) },
      {
        titulo: 'Cálculo', tipo: 'texto', ancho: 20, anchoPdf: 74,
        valor: (r) => CALCULO_FONACOT_LABEL[r.calculoRetencion] || r.calculoRetencion || '',
      },
      { titulo: 'Importe Crédito', tipo: 'moneda', ancho: 15, anchoPdf: 62, valor: (r) => r.importeCredito },
      { titulo: 'Importe/Periodo', tipo: 'moneda', ancho: 15, anchoPdf: 62, valor: (r) => r.importeFijoPeriodo },
      { titulo: 'Acumulado', tipo: 'moneda', ancho: 14, anchoPdf: 60, valor: (r) => r.montoAcumulado },
      { titulo: 'Saldo Actual', tipo: 'moneda', ancho: 14, anchoPdf: 60, valor: (r) => r.saldoActual },
      // Sin fecha de aplicación el crédito es invisible para el motor: se dice, no se deja en blanco.
      {
        titulo: 'F. Aplicación', tipo: 'centro', ancho: 22, anchoPdf: 72,
        valor: (r) => (r.sinFechaAplicacion ? 'Sin fecha — no se aplicará' : fmtFecha(r.fechaAplicacion)),
      },
      colExportEstatus(ESTATUS_ACTIVO_INACTIVO),
    ],
  },

  {
    clave: 'movimientos',
    titulo: 'Mov. Permanentes',
    endpointListado: 'nomina/movimientosPermanentesEmpresa',
    endpointCancelar: 'nomina/cancelarMovimientoPermanente',
    cuerpoCancelar: (r, empresaId) => ({ movimientoId: r._id, empresaId }),
    tituloCancelar: '¿Inactivar este movimiento permanente?',
    textoCancelar: 'Ya no se aplicará en futuros períodos.',
    // Un movimiento completado no se puede inactivar: el backend lo rechaza.
    puedeCancelar: (r) => r.estatus === 1,
    etiquetasEstatus: ESTATUS_CON_COMPLETADO,
    opcionesEstatus: [...OPCIONES_ACTIVO_INACTIVO, { valor: '2', label: 'Completados' }],
    soportaVigentes: true,
    ayudaVigentes: 'Sólo los que ya alcanzaron su fecha de inicio',
    anchoTabla: 1700,
    filtrosExtra: (ctx) => [
      {
        clave: 'tipoConcepto',
        placeholder: 'Percepción / Deducción',
        opciones: Object.entries(TIPO_CONCEPTO_LABEL).map(([valor, label]) => ({ valor, label })),
      },
      {
        clave: 'conceptoId',
        placeholder: 'Todos los conceptos',
        ancho: 220,
        opciones: (ctx.conceptos || []).map((c) => ({
          valor: c._id,
          label: `${c.serieNumero || c.numeroConcepto || ''} - ${c.descripcion || ''}`.trim(),
        })),
      },
    ],
    columnas: () => [
      COL_EMPLEADO,
      { title: 'Descripción', dataIndex: 'descripcion', key: 'desc', width: 180, ellipsis: true },
      {
        title: 'Tipo', dataIndex: 'tipoConcepto', key: 'tipoConcepto', width: 110,
        render: (v) => TIPO_CONCEPTO_LABEL[v] || v || '—',
      },
      { title: 'Concepto', key: 'concepto', width: 190, ellipsis: true, render: (_, r) => nombreConcepto(r) },
      {
        title: 'Tipo Valor', dataIndex: 'tipoValor', key: 'tipoValor', width: 100,
        render: (v) => TIPO_VALOR_LABEL[v] || v || '—',
      },
      {
        title: 'Importe', dataIndex: 'importe', key: 'importe', width: 120, align: 'right',
        // En tipoValor 'valor' el campo lleva días/unidades, no pesos.
        render: (v, r) => (r.tipoValor === 'valor' ? fmtNum(v) : fmtMXN(v)),
      },
      {
        title: 'V. Aplicar', dataIndex: 'vecesAplicar', key: 'vecesAplicar', width: 90, align: 'right',
        render: (v) => (v > 0 ? v : '∞'),
      },
      {
        title: 'V. Aplicado', dataIndex: 'vecesAplicado', key: 'vecesAplicado',
        width: 95, align: 'right', render: (v) => v ?? 0,
      },
      {
        title: 'M. Límite', dataIndex: 'montoLimite', key: 'montoLimite', width: 120, align: 'right',
        render: (v) => (v ? fmtMXN(v) : '—'),
      },
      {
        title: 'M. Acumulado', dataIndex: 'montoAcumulado', key: 'montoAcumulado',
        width: 125, align: 'right', render: fmtMXN,
      },
      { title: 'F. Inicio', dataIndex: 'fechaInicio', key: 'fechaInicio', width: 105, render: fmtFecha },
      {
        title: 'No. Control', dataIndex: 'numeroControl', key: 'numeroControl', width: 130,
        render: (v) => v || '—',
      },
      colEstatus(ESTATUS_CON_COMPLETADO),
    ],
    columnasExport: () => [
      ...COLS_EXPORT_EMPLEADO,
      { titulo: 'Descripción', tipo: 'texto', ancho: 24, anchoPdf: 74, valor: (r) => r.descripcion },
      {
        titulo: 'Tipo', tipo: 'texto', ancho: 13, anchoPdf: 46,
        valor: (r) => TIPO_CONCEPTO_LABEL[r.tipoConcepto] || r.tipoConcepto || '',
      },
      { titulo: 'Concepto', tipo: 'texto', ancho: 24, anchoPdf: 78, valor: (r) => nombreConcepto(r) },
      {
        titulo: 'Tipo Valor', tipo: 'centro', ancho: 12, anchoPdf: 40,
        valor: (r) => TIPO_VALOR_LABEL[r.tipoValor] || r.tipoValor || '',
      },
      // En tipoValor 'valor' el campo lleva días/unidades: ni se formatea como pesos ni entra al total.
      {
        titulo: 'Importe', tipo: (r) => (r.tipoValor === 'valor' ? 'numero' : 'moneda'),
        ancho: 14, anchoPdf: 55, valor: (r) => r.importe,
      },
      {
        titulo: 'V. Aplicar', tipo: 'centro', ancho: 10, anchoPdf: 36,
        valor: (r) => (r.vecesAplicar > 0 ? String(r.vecesAplicar) : '∞'),
      },
      { titulo: 'V. Aplicado', tipo: 'numero', ancho: 11, anchoPdf: 40, valor: (r) => r.vecesAplicado ?? 0 },
      { titulo: 'M. Límite', tipo: 'moneda', ancho: 14, anchoPdf: 52, valor: (r) => r.montoLimite },
      { titulo: 'M. Acumulado', tipo: 'moneda', ancho: 14, anchoPdf: 55, valor: (r) => r.montoAcumulado },
      { titulo: 'F. Inicio', tipo: 'fecha', ancho: 12, anchoPdf: 46, valor: (r) => r.fechaInicio },
      { titulo: 'No. Control', tipo: 'texto', ancho: 14, anchoPdf: 48, valor: (r) => r.numeroControl },
      colExportEstatus(ESTATUS_CON_COMPLETADO),
    ],
  },

  {
    clave: 'incidencias',
    titulo: 'Incid. Permanentes',
    endpointListado: 'nomina/incidenciasPermanentesEmpresa',
    endpointCancelar: 'nomina/cancelarIncidenciaPermanente',
    cuerpoCancelar: (r, empresaId) => ({ incidenciaId: r._id, empresaId }),
    tituloCancelar: '¿Inactivar esta incidencia permanente?',
    textoCancelar: 'Dejará de generarse en los próximos cálculos. Lo ya generado se quita al recalcular.',
    etiquetasEstatus: { 1: { texto: 'Activa', color: 'green' }, 0: { texto: 'Inactiva' } },
    opcionesEstatus: [{ valor: '1', label: 'Activas' }, { valor: '0', label: 'Inactivas' }],
    soportaVigentes: true,
    ayudaVigentes: 'Sólo las vigentes hoy (ya iniciadas y sin fecha fin pasada)',
    anchoTabla: 1300,
    filtrosExtra: (ctx) => [
      {
        clave: 'tipoIncidenciaId',
        placeholder: 'Todas las incidencias',
        ancho: 220,
        opciones: (ctx.tiposIncidencias || []).map((t) => ({
          valor: t._id,
          label: `${t.clave || ''} - ${t.nombre || ''}`.trim(),
        })),
      },
      {
        clave: 'tipoPatron',
        placeholder: 'Todos los patrones',
        opciones: Object.entries(TIPO_PATRON_LABEL).map(([valor, label]) => ({ valor, label })),
      },
    ],
    columnas: () => [
      COL_EMPLEADO,
      {
        title: 'Descripción', dataIndex: 'descripcion', key: 'desc', width: 180, ellipsis: true,
        render: (v) => v || '—',
      },
      { title: 'Incidencia', key: 'tipo', width: 200, ellipsis: true, render: (_, r) => nombreTipoIncidencia(r) },
      {
        title: 'Patrón', dataIndex: 'tipoPatron', key: 'patron', width: 140,
        render: (v) => TIPO_PATRON_LABEL[v] || v || '—',
      },
      { title: 'Días', key: 'dias', width: 230, ellipsis: true, render: (_, r) => resumenPatron(r) },
      {
        title: 'Total', dataIndex: 'totalPatron', key: 'total', width: 80, align: 'right',
        render: (v) => (v ?? 0),
      },
      {
        title: 'Vigencia', key: 'vigencia', width: 180,
        render: (_, r) => `${fmtFecha(r.fechaInicio)} → ${r.fechaFin ? fmtFecha(r.fechaFin) : '∞'}`,
      },
      colEstatus({ 1: { texto: 'Activa', color: 'green' }, 0: { texto: 'Inactiva' } }),
    ],
    columnasExport: () => [
      ...COLS_EXPORT_EMPLEADO,
      { titulo: 'Descripción', tipo: 'texto', ancho: 26, anchoPdf: 100, valor: (r) => r.descripcion },
      { titulo: 'Incidencia', tipo: 'texto', ancho: 28, anchoPdf: 110, valor: (r) => nombreTipoIncidencia(r) },
      {
        titulo: 'Patrón', tipo: 'texto', ancho: 18, anchoPdf: 74,
        valor: (r) => TIPO_PATRON_LABEL[r.tipoPatron] || r.tipoPatron || '',
      },
      { titulo: 'Días', tipo: 'texto', ancho: 30, anchoPdf: 130, valor: (r) => resumenPatron(r) },
      { titulo: 'Total', tipo: 'numero', ancho: 9, anchoPdf: 40, valor: (r) => r.totalPatron ?? 0 },
      // En pantalla la vigencia es un solo texto "inicio → fin"; en una hoja dos columnas de fecha
      // se pueden ordenar y filtrar, así que aquí se parte.
      { titulo: 'F. Inicio', tipo: 'fecha', ancho: 12, anchoPdf: 52, valor: (r) => r.fechaInicio },
      {
        titulo: 'F. Fin', tipo: 'centro', ancho: 12, anchoPdf: 52,
        valor: (r) => (r.fechaFin ? fmtFecha(r.fechaFin) : '∞'),
      },
      colExportEstatus({ 1: { texto: 'Activa' }, 0: { texto: 'Inactiva' } }),
    ],
  },

  {
    clave: 'incapacidades',
    titulo: 'Incapacidades',
    endpointListado: 'nomina/incapacidadesEmpresa',
    endpointCancelar: 'nomina/cancelarIncapacidad',
    cuerpoCancelar: (r, empresaId) => ({ incapacidadId: r._id, empresaId }),
    tituloCancelar: '¿Cancelar esta incapacidad?',
    textoCancelar: 'Después tienes que recalcular la nómina para que se refleje.',
    etiquetasEstatus: ESTATUS_ACTIVO_INACTIVO,
    opcionesEstatus: [{ valor: '1', label: 'Activas' }, { valor: '0', label: 'Canceladas' }],
    soportaVigentes: true,
    ayudaVigentes: 'Sólo las que cubren el día de hoy',
    anchoTabla: 1500,
    filtrosExtra: (ctx) => [
      {
        clave: 'tipoIncidenciaId',
        placeholder: 'Todos los tipos',
        ancho: 220,
        opciones: (ctx.tiposIncidencias || []).map((t) => ({
          valor: t._id,
          label: `${t.clave || ''} - ${t.nombre || ''}`.trim(),
        })),
      },
      {
        clave: 'ramoSeguro',
        placeholder: 'Todos los ramos',
        ancho: 220,
        opciones: Object.entries(RAMO_SEGURO_LABEL).map(([valor, label]) => ({ valor, label })),
      },
    ],
    columnas: () => [
      COL_EMPLEADO,
      { title: 'Folio', dataIndex: 'folio', key: 'folio', width: 130 },
      {
        title: 'Tipo', key: 'tipo', width: 190, ellipsis: true,
        render: (_, r) => r.tipoIncidenciaNombre || nombreTipoIncidencia(r),
      },
      {
        title: 'Días', dataIndex: 'diasAutorizados', key: 'dias', width: 80, align: 'right',
        render: fmtNum,
      },
      { title: 'F. Inicio', dataIndex: 'fechaInicio', key: 'fechaInicio', width: 105, render: fmtFecha },
      { title: 'F. Fin', dataIndex: 'fechaFin', key: 'fechaFin', width: 105, render: fmtFecha },
      {
        title: 'Ramo', dataIndex: 'ramoSeguro', key: 'ramo', width: 200,
        render: (v) => RAMO_SEGURO_LABEL[v] || v || '—',
      },
      {
        title: 'Salario Diario', dataIndex: 'salarioDiario', key: 'salarioDiario',
        width: 125, align: 'right', render: fmtMXN,
      },
      {
        title: 'Importe', dataIndex: 'importeMonetario', key: 'importe',
        width: 120, align: 'right', render: fmtMXN,
      },
      colEstatus(ESTATUS_ACTIVO_INACTIVO),
    ],
    columnasExport: () => [
      ...COLS_EXPORT_EMPLEADO,
      { titulo: 'Folio', tipo: 'texto', ancho: 15, anchoPdf: 62, valor: (r) => r.folio },
      {
        titulo: 'Tipo', tipo: 'texto', ancho: 26, anchoPdf: 105,
        valor: (r) => r.tipoIncidenciaNombre || nombreTipoIncidencia(r),
      },
      { titulo: 'Días', tipo: 'numero', ancho: 9, anchoPdf: 40, suma: true, valor: (r) => r.diasAutorizados },
      { titulo: 'F. Inicio', tipo: 'fecha', ancho: 12, anchoPdf: 52, valor: (r) => r.fechaInicio },
      { titulo: 'F. Fin', tipo: 'fecha', ancho: 12, anchoPdf: 52, valor: (r) => r.fechaFin },
      {
        titulo: 'Ramo', tipo: 'texto', ancho: 26, anchoPdf: 105,
        valor: (r) => RAMO_SEGURO_LABEL[r.ramoSeguro] || r.ramoSeguro || '',
      },
      { titulo: 'Salario Diario', tipo: 'moneda', ancho: 14, anchoPdf: 60, valor: (r) => r.salarioDiario },
      { titulo: 'Importe', tipo: 'moneda', ancho: 14, anchoPdf: 60, valor: (r) => r.importeMonetario },
      colExportEstatus({ 1: { texto: 'Activa' }, 0: { texto: 'Cancelada' } }),
    ],
  },

  {
    clave: 'vacaciones',
    titulo: 'Vacaciones',
    endpointListado: 'nomina/movimientosVacacionesEmpresa',
    endpointCancelar: 'nomina/cancelarVacacion',
    // Este endpoint sí necesita el periodo: la cancelación devuelve los días al kárdex de ese periodo.
    cuerpoCancelar: (r, empresaId) => ({ movimientoId: r._id, periodoId: r.periodoId, empresaId }),
    tituloCancelar: '¿Cancelar este movimiento de vacaciones?',
    textoCancelar: 'Los días regresarán al saldo disponible. Después hay que recalcular la nómina.',
    etiquetasEstatus: { 1: { texto: 'Activo', color: 'green' }, 0: { texto: 'Cancelado' } },
    opcionesEstatus: [{ valor: '1', label: 'Activos' }, { valor: '0', label: 'Cancelados' }],
    soportaVigentes: false,
    anchoTabla: 1500,
    // Vacaciones masivas ya tiene su propia pantalla; desde aquí sólo se enlaza.
    enlaceExtra: { texto: 'Vacaciones masivas', ruta: '/panel/vacaciones' },
    filtrosExtra: () => [
      {
        clave: 'tipo',
        placeholder: 'Todos los tipos',
        ancho: 180,
        opciones: Object.entries(TIPO_VACACION_LABEL).map(([valor, label]) => ({ valor, label })),
      },
    ],
    columnas: () => [
      COL_EMPLEADO,
      {
        title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 140,
        render: (v) => TIPO_VACACION_LABEL[v] || v || '—',
      },
      {
        title: 'Periodo', key: 'periodo', width: 150,
        render: (_, r) => (r.periodo ? `${r.periodo.serie}-${r.periodo.numero}` : '—'),
      },
      { title: 'F. Inicio', dataIndex: 'fechaInicio', key: 'fechaInicio', width: 105, render: fmtFecha },
      { title: 'F. Fin', dataIndex: 'fechaFin', key: 'fechaFin', width: 105, render: fmtFecha },
      { title: 'F. Pago', dataIndex: 'fechaPago', key: 'fechaPago', width: 105, render: fmtFecha },
      {
        title: 'Días Vac.', dataIndex: 'diasVacaciones', key: 'diasVacaciones',
        width: 90, align: 'right', render: fmtNum,
      },
      {
        title: 'Días Prima', dataIndex: 'diasPrima', key: 'diasPrima',
        width: 95, align: 'right', render: fmtNum,
      },
      {
        title: 'Importe Total', dataIndex: 'importeTotal', key: 'importeTotal',
        width: 130, align: 'right', render: fmtMXN,
      },
      colEstatus({ 1: { texto: 'Activo', color: 'green' }, 0: { texto: 'Cancelado' } }),
    ],
    columnasExport: () => [
      ...COLS_EXPORT_EMPLEADO,
      {
        titulo: 'Tipo', tipo: 'texto', ancho: 18, anchoPdf: 74,
        valor: (r) => TIPO_VACACION_LABEL[r.tipo] || r.tipo || '',
      },
      {
        titulo: 'Periodo', tipo: 'centro', ancho: 15, anchoPdf: 62,
        valor: (r) => (r.periodo ? `${r.periodo.serie}-${r.periodo.numero}` : ''),
      },
      { titulo: 'F. Inicio', tipo: 'fecha', ancho: 12, anchoPdf: 52, valor: (r) => r.fechaInicio },
      { titulo: 'F. Fin', tipo: 'fecha', ancho: 12, anchoPdf: 52, valor: (r) => r.fechaFin },
      { titulo: 'F. Pago', tipo: 'fecha', ancho: 12, anchoPdf: 52, valor: (r) => r.fechaPago },
      { titulo: 'Días Vac.', tipo: 'numero', ancho: 10, anchoPdf: 46, suma: true, valor: (r) => r.diasVacaciones },
      { titulo: 'Días Prima', tipo: 'numero', ancho: 11, anchoPdf: 48, suma: true, valor: (r) => r.diasPrima },
      { titulo: 'Importe Total', tipo: 'moneda', ancho: 15, anchoPdf: 64, valor: (r) => r.importeTotal },
      colExportEstatus({ 1: { texto: 'Activo' }, 0: { texto: 'Cancelado' } }),
    ],
  },
]

export const DEFINICION_POR_CLAVE = DEFINICIONES.reduce((acc, d) => {
  acc[d.clave] = d
  return acc
}, {})
